import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./client";

/**
 * Addy Geo proxy client.
 *
 * <p>Vietnam moved to a 2-level address model (province + ward; districts
 * removed). HIS stores only the Addy Geo `national_code` for province and
 * ward — names are resolved at read-time via this proxy, which is served
 * from an in-memory snapshot (static data → cache forever).
 *
 * <p>IMPORTANT code-type rule: the geo `code` is a **number** in JSON, but
 * Patient/Branch persist codes as **strings** (varchar). So when SAVING send
 * `String(code)`, and when RESOLVING a stored string to a name look it up in
 * a Map keyed by `String(code)`. Always compare/lookup as strings.
 */

export interface GeoOption {
  code: number;
  name: string;
}

export interface GeoWard {
  code: number;
  name: string;
  provinceCode: number;
}

export const geoApi = {
  provinces(): Promise<GeoOption[]> {
    return apiClient.get<GeoOption[]>("/v1/geo/provinces").then((r) => r.data);
  },
  wards(provinceCode: number | string): Promise<GeoOption[]> {
    return apiClient
      .get<GeoOption[]>(`/v1/geo/provinces/${provinceCode}/wards`)
      .then((r) => r.data);
  },
  allWards(): Promise<GeoWard[]> {
    return apiClient.get<GeoWard[]>("/v1/geo/wards").then((r) => r.data);
  },
};

export const geoQueryKeys = {
  provinces: () => ["geo", "provinces"] as const,
  wards: (code: number | string | null | undefined) =>
    ["geo", "wards", code == null ? null : String(code)] as const,
  allWards: () => ["geo", "wards", "all"] as const,
};

/** Geo data is static — cache effectively forever within a session. */
const GEO_STALE = Number.POSITIVE_INFINITY;

/**
 * Loads all provinces + all wards once and returns two Maps keyed by
 * `String(code)` -> name, for O(1) resolution of stored (string) codes when
 * rendering lists. `ready` is true once both queries have resolved.
 */
export function useGeoNames(): {
  provinceName: Map<string, string>;
  wardName: Map<string, string>;
  ready: boolean;
} {
  const provincesQuery = useQuery({
    queryKey: geoQueryKeys.provinces(),
    queryFn: () => geoApi.provinces(),
    staleTime: GEO_STALE,
    gcTime: GEO_STALE,
  });
  const wardsQuery = useQuery({
    queryKey: geoQueryKeys.allWards(),
    queryFn: () => geoApi.allWards(),
    staleTime: GEO_STALE,
    gcTime: GEO_STALE,
  });

  const provinceName = new Map<string, string>();
  for (const p of provincesQuery.data ?? []) {
    provinceName.set(String(p.code), p.name);
  }
  const wardName = new Map<string, string>();
  for (const w of wardsQuery.data ?? []) {
    wardName.set(String(w.code), w.name);
  }

  return {
    provinceName,
    wardName,
    ready: provincesQuery.isSuccess && wardsQuery.isSuccess,
  };
}
